import React, { useMemo, useState } from 'react';
import { useData } from '../../data/DataProvider';
import { useToast } from '../../shared/components/ToastProvider';
import EmptyState from '../../shared/components/EmptyState';
import SlideOver from '../../shared/components/SlideOver';
import SafeIcon from '../../common/SafeIcon';
import { Icons } from '../../shared/icons';
import { money, date } from '../../shared/formatters';
import { exportCsv } from '../../shared/importExport';

const TYPES = ['mileage', 'fuel', 'toll', 'other'];
const TYPE_LABEL = { mileage: 'Mileage', fuel: 'Fuel', toll: 'Toll', other: 'Other' };

// Admin expense review + CSV export (update_tasks.md §7.7). Read-only:
// update/delete exist in the provider but are deliberately not exposed in
// this pass. Mileage miles/rate fields deferred per TBC-10.
export default function Expenses() {
  const { data } = useData();
  const { notify } = useToast();
  const [engineer, setEngineer] = useState('all');
  const [type, setType] = useState('all');
  const [from, setFrom] = useState('');
  const [to, setTo] = useState('');
  const [selected, setSelected] = useState(null);

  const engineerName = (engineerId) => data.team.find((member) => member.id === engineerId)?.name
    || data.engineers.find((person) => person.id === engineerId)?.name || 'Unknown';

  const jobTitle = (jobId) => data.jobs.find((job) => job.id === jobId)?.title || '';

  const engineersWithExpenses = useMemo(
    () => Array.from(new Set((data.expenses || []).map((expense) => expense.engineer_id).filter(Boolean))),
    [data.expenses],
  );

  const filtered = useMemo(() => (data.expenses || []).filter((expense) => {
    if (engineer !== 'all' && expense.engineer_id !== engineer) return false;
    if (type !== 'all' && expense.type !== type) return false;
    if (from && expense.date && expense.date < from) return false;
    if (to && expense.date && expense.date > to) return false;
    return true;
  }), [data.expenses, engineer, type, from, to]);

  const total = filtered.reduce((sum, expense) => sum + (Number(expense.amount) || 0), 0);

  const perEngineer = useMemo(() => {
    const byEngineer = {};
    filtered.forEach((expense) => {
      byEngineer[expense.engineer_id] = (byEngineer[expense.engineer_id] || 0) + (Number(expense.amount) || 0);
    });
    return byEngineer;
  }, [filtered]);

  const exportCsvRows = () => {
    exportCsv('expenses', filtered, [
      { label: 'Date', value: (row) => row.date },
      { label: 'Type', value: (row) => TYPE_LABEL[row.type] || row.type },
      { label: 'Amount', value: (row) => row.amount },
      { label: 'Engineer', value: (row) => engineerName(row.engineer_id) },
      { label: 'Job', value: (row) => jobTitle(row.job_id) },
      { label: 'Notes', value: (row) => row.notes },
    ]);
    notify('CSV exported');
  };

  return (
    <div className="standalone-page">
      <header className="simple-header">
        <div>
          <span className="eyebrow">Engineer</span>
          <h1>Expenses</h1>
          <p className="field-hint">
            {filtered.length} expense{filtered.length === 1 ? '' : 's'} · total {money(total)}
          </p>
        </div>
        <div className="header-actions">
          <button className="icon-button" title="Export filtered expenses (CSV)" aria-label="Export CSV" onClick={exportCsvRows}>
            <SafeIcon icon={Icons.Download} />
          </button>
        </div>
      </header>

      {Object.keys(perEngineer).length > 0 && (
        <div className="stats-grid">
          {Object.entries(perEngineer).map(([engineerId, amount]) => (
            <div className="stat-card" key={engineerId}>
              <span className="eyebrow">{engineerName(engineerId)}</span>
              <strong>{money(amount)}</strong>
            </div>
          ))}
        </div>
      )}

      <div className="toolbar">
        <select value={engineer} onChange={(event) => setEngineer(event.target.value)} className="engineer-filter" aria-label="Filter by engineer">
          <option value="all">All engineers</option>
          {engineersWithExpenses.map((engineerId) => <option key={engineerId} value={engineerId}>{engineerName(engineerId)}</option>)}
        </select>
        <select value={type} onChange={(event) => setType(event.target.value)} className="engineer-filter" aria-label="Filter by type">
          <option value="all">All types</option>
          {TYPES.map((item) => <option key={item} value={item}>{TYPE_LABEL[item]}</option>)}
        </select>
        <div className="range-filter">
          <input type="date" value={from} onChange={(event) => setFrom(event.target.value)} aria-label="From date" />
          <span>→</span>
          <input type="date" value={to} onChange={(event) => setTo(event.target.value)} aria-label="To date" />
        </div>
      </div>

      {!data.expenses.length ? (
        <EmptyState title="No expenses yet" message="Expenses logged by engineers appear here." />
      ) : !filtered.length ? (
        <EmptyState title="No matching expenses" message="Try different filters." />
      ) : (
        <div className="expense-list">
          {filtered.map((expense) => (
            <button type="button" className="expense-item" key={expense.id} onClick={() => setSelected(expense)}>
              <span className="expense-type">{TYPE_LABEL[expense.type] || expense.type}</span>
              <span className="expense-amount">{money(expense.amount)}</span>
              <span className="expense-note">{engineerName(expense.engineer_id)}{expense.notes ? ` — ${expense.notes}` : ''}</span>
              <small className="field-hint">{date(expense.date)}{jobTitle(expense.job_id) ? ` · ${jobTitle(expense.job_id)}` : ''}</small>
            </button>
          ))}
        </div>
      )}

      <SlideOver open={Boolean(selected)} title="Expense detail" onClose={() => setSelected(null)}>
        {selected && (
          <div className="drawer-form">
            <div className="detail-panel">
              <dl className="spec-table">
                <div><dt>Engineer</dt><dd>{engineerName(selected.engineer_id)}</dd></div>
                <div><dt>Type</dt><dd>{TYPE_LABEL[selected.type] || selected.type}</dd></div>
                <div><dt>Amount</dt><dd>{money(selected.amount)}</dd></div>
                <div><dt>Date</dt><dd>{date(selected.date)}</dd></div>
                <div><dt>Linked job</dt><dd>{jobTitle(selected.job_id) || '—'}</dd></div>
                <div><dt>Notes</dt><dd>{selected.notes || '—'}</dd></div>
              </dl>
            </div>
          </div>
        )}
      </SlideOver>
    </div>
  );
}
